import React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileCheck,
  FileText,
  Loader2,
  ShieldCheck,
  X,
} from 'lucide-react';
import { LitigationPdfCountdown } from './LitigationView';

export interface VertragQAStatistik {
  ok?: number;
  warnung?: number;
  fehler?: number;
}

export interface VertragQAResult {
  type: 'success' | 'error';
  pdfUrl?: string;
  pdfLinkExpiresAt?: string;
  pdfGenerationNote?: string;
  gesamtstatus?: string;
  statistik?: VertragQAStatistik;
  llmBeurteilung?: string;
  risikostufe?: string;
}

export interface VertragsQAViewProps {
  preisliste: File | null;
  onPreislisteChange: (file: File | null) => void;
  gruendungspaket: File | null;
  onGruendungspaketChange: (file: File | null) => void;
  markeIp: File | null;
  onMarkeIpChange: (file: File | null) => void;
  optIn: boolean;
  onOptInChange: (checked: boolean) => void;
  uploading: boolean;
  result: VertragQAResult | null;
  onResultClear: () => void;
  onUpload: () => void;
}

function gesamtStatusUi(status?: string): {
  txt: string;
  panelClass: string;
  titleClass: string;
  iconClass: string;
} {
  if (status === 'freigegeben') {
    return {
      txt: '✅ Bereit zur anwaltlichen Prüfung',
      panelClass: 'bg-emerald-500/10 border-emerald-500/30',
      titleClass: 'text-emerald-400',
      iconClass: 'text-emerald-400',
    };
  }
  if (status === 'warnung_pruefen') {
    return {
      txt: '⚠️ Warnungen – vor Termin prüfen',
      panelClass: 'bg-amber-500/10 border-amber-500/30',
      titleClass: 'text-amber-400',
      iconClass: 'text-amber-400',
    };
  }
  if (status === 'nicht_freigegeben') {
    return {
      txt: '❌ Prüfung nicht bestanden',
      panelClass: 'bg-red-500/10 border-red-500/30',
      titleClass: 'text-red-400',
      iconClass: 'text-red-400',
    };
  }
  return {
    txt: 'Prüfung abgeschlossen',
    panelClass: 'bg-blue-500/10 border-blue-500/30',
    titleClass: 'text-blue-400',
    iconClass: 'text-blue-400',
  };
}

function VqaFileSlot({
  label,
  hint,
  file,
  onChange,
}: {
  label: string;
  hint: string;
  file: File | null;
  onChange: (f: File | null) => void;
}) {
  return (
    <div>
      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
        {label}
      </label>
      <p className="text-slate-500 text-xs mb-2">{hint}</p>
      {file ? (
        <div className="flex items-center justify-between bg-slate-800/60 border border-violet-500/40 rounded-xl px-3 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <FileCheck className="w-4 h-4 text-violet-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-white text-xs font-medium truncate max-w-[220px]">{file.name}</p>
              <p className="text-slate-500 text-[10px]">{(file.size / 1024 / 1024).toFixed(1)} MB</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-red-400 hover:text-red-300 ml-2 flex-shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <label className="flex items-center gap-3 border border-dashed border-slate-700 hover:border-violet-500/50 rounded-xl px-4 py-3 cursor-pointer transition-all group">
          <input
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={(e) => onChange(e.target.files?.[0] || null)}
          />
          <FileText className="w-5 h-5 text-slate-600 group-hover:text-violet-400 flex-shrink-0 transition-colors" />
          <span className="text-slate-500 group-hover:text-slate-300 text-xs transition-colors">
            PDF auswählen (max. 20 MB)
          </span>
        </label>
      )}
    </div>
  );
}

export const VertragsQAView: React.FC<VertragsQAViewProps> = ({
  preisliste,
  onPreislisteChange,
  gruendungspaket,
  onGruendungspaketChange,
  markeIp,
  onMarkeIpChange,
  optIn,
  onOptInChange,
  uploading,
  result,
  onResultClear,
  onUpload,
}) => {
  const hasAtLeastOne = preisliste || gruendungspaket || markeIp;
  const gesamtInfo = gesamtStatusUi(result?.gesamtstatus);

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-white">Vertragsprüfung</h1>
        <p className="text-violet-400/80 text-sm font-medium mt-1">Hilfestellung · Keine Rechtsberatung</p>
        <p className="text-slate-400 mt-1">
          Dokumente hochladen · automatische Konsistenz- und Vollständigkeitsprüfung · PDF-Report
        </p>
      </div>

      {result?.type === 'success' && (
        <div className={`border rounded-2xl p-5 space-y-3 ${gesamtInfo.panelClass}`}>
          <div className="flex items-start gap-3">
            <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${gesamtInfo.iconClass}`} />
            <div className="flex-1 min-w-0">
              <p className={`font-bold ${gesamtInfo.titleClass}`}>{gesamtInfo.txt}</p>
              {result.statistik && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {(result.statistik.ok ?? 0) > 0 && (
                    <span className="text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded-lg">
                      {result.statistik.ok} OK
                    </span>
                  )}
                  {(result.statistik.warnung ?? 0) > 0 && (
                    <span className="text-xs bg-orange-500/20 text-orange-400 border border-orange-500/30 px-2 py-0.5 rounded-lg font-bold">
                      {result.statistik.warnung} Warnungen
                    </span>
                  )}
                  {(result.statistik.fehler ?? 0) > 0 && (
                    <span className="text-xs bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-0.5 rounded-lg font-bold">
                      {result.statistik.fehler} Fehler
                    </span>
                  )}
                  {result.risikostufe && (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-lg font-bold border ${
                        result.risikostufe === 'ROT'
                          ? 'bg-red-500/20 text-red-400 border-red-500/30'
                          : result.risikostufe === 'GELB'
                            ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                            : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                      }`}
                    >
                      Risiko: {result.risikostufe}
                    </span>
                  )}
                </div>
              )}
              {result.llmBeurteilung && (
                <p className="text-slate-300 text-xs mt-3 leading-relaxed border-t border-slate-700/50 pt-3">
                  {result.llmBeurteilung}
                </p>
              )}
            </div>
          </div>
          {result.pdfUrl ? (
            <>
              <a
                href={result.pdfUrl}
                target="_blank"
                rel="noreferrer"
                download
                className="flex items-center justify-center gap-2 w-full bg-violet-600 hover:bg-violet-500 text-white font-bold py-2.5 rounded-xl transition-colors text-sm"
              >
                <Download className="w-4 h-4" /> QA-Report PDF herunterladen
              </a>
              <LitigationPdfCountdown expiresAtIso={result.pdfLinkExpiresAt} />
            </>
          ) : (
            <div className="space-y-2">
              <p className="text-slate-400 text-xs">PDF wird vorbereitet.</p>
              <LitigationPdfCountdown expiresAtIso={result.pdfLinkExpiresAt} />
              {result.pdfGenerationNote && (
                <p className="text-amber-200/80 text-xs font-mono bg-amber-950/40 border border-amber-500/20 rounded-lg p-2 break-words">
                  {result.pdfGenerationNote}
                </p>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={onResultClear}
            className="w-full text-slate-400 hover:text-white text-sm py-1 transition-colors"
          >
            Neue Prüfung starten
          </button>
        </div>
      )}

      {result?.type === 'error' && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-5 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 font-bold">Fehler beim Upload</p>
            <p className="text-slate-300 text-sm mt-1">
              Bitte erneut versuchen oder kontaktieren Sie{' '}
              <a href="mailto:office@arkorbit.at" className="text-blue-400 underline">
                office@arkorbit.at
              </a>
            </p>
          </div>
        </div>
      )}

      <div
        className={`bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-5${uploading ? ' opacity-90 pointer-events-none' : ''}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <FileCheck className="w-5 h-5 text-violet-400" />
          <span className="text-sm font-bold text-white">Dokumente hochladen</span>
          <span className="text-xs text-slate-500 ml-1">– mindestens 1 Dokument erforderlich</span>
        </div>

        <VqaFileSlot
          label="Preisliste"
          hint="Preise und Leistungsumfang."
          file={preisliste}
          onChange={onPreislisteChange}
        />
        <VqaFileSlot
          label="Gründungspaket"
          hint="AGB, Auftragsverarbeitung und technische und organisatorische Maßnahmen."
          file={gruendungspaket}
          onChange={onGruendungspaketChange}
        />
        <VqaFileSlot
          label="Marke/Kennzeichen"
          hint="Wortmarke, Slogan, Logo und Schutzklassen."
          file={markeIp}
          onChange={onMarkeIpChange}
        />

        <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={optIn}
              onChange={(e) => onOptInChange(e.target.checked)}
              className="mt-0.5 accent-violet-500"
            />
            <span className="text-slate-300 text-sm leading-relaxed">
              Ich bestätige, dass ich berechtigt bin, diese Dokumente hochzuladen. Ich verstehe, dass diese Prüfung
              eine <strong>technische Hilfestellung</strong> darstellt und{' '}
              <strong>keine anwaltliche Rechtsberatung</strong> (§ 9 RAO) ersetzt. Die Ergebnisse sind vor Verwendung
              eigenverantwortlich zu prüfen.
            </span>
          </label>
        </div>

        <button
          type="button"
          onClick={onUpload}
          disabled={!hasAtLeastOne || !optIn || uploading}
          className="w-full bg-violet-600 hover:bg-violet-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white disabled:text-slate-500 font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Prüfung läuft…
            </>
          ) : (
            <>
              <FileCheck className="w-4 h-4" /> Qualitätsprüfung starten
            </>
          )}
        </button>

        <div className="bg-slate-800/40 rounded-xl px-4 py-3 flex items-start gap-3">
          <ShieldCheck className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
          <p className="text-slate-400 text-xs leading-relaxed">
            Ihre Dokumente werden verschlüsselt übertragen und auf dem ARKORBIT-Server in Frankfurt (EU) verarbeitet.
            Das Prüf-PDF wird bereitgestellt und nach 30 Minuten automatisch gelöscht.
          </p>
        </div>
      </div>

      <div className="bg-amber-500/5 border border-amber-500/15 rounded-2xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-slate-500 text-xs leading-relaxed">
          <strong className="text-amber-400/90">Hinweis:</strong> Die Vertragsprüfung prüft{' '}
          <strong>formale Konsistenz und Vollständigkeit</strong> (regelbasiert + KI-Hilfestellung). Er prüft{' '}
          <strong>nicht</strong>, ob einzelne Klauseln rechtlich wirksam sind – das ist Aufgabe des Rechtsanwalts.
        </p>
      </div>
    </div>
  );
};
